import { IconError } from "@/assets/icons/geist/IconError";
import { StepState } from "@/components/environments/environment-start-steps-state";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { useEffect, useMemo, useState, type FC } from "react";

export const StartSequenceStepIcon: FC<{ state: StepState } & PropsWithClassName> = ({ state, className }) => {
    // This ensures we only animate state transitions, and not the initial render.
    // The state is reset per environment by passing a key prop to this component.
    const [initial] = useState(state);
    const [hasTransitioned, setHasTransitioned] = useState(false);
    useEffect(() => {
        if (state !== initial) {
            setHasTransitioned(true);
        }
    }, [state, initial]);
    const animate = useMemo(() => hasTransitioned || state !== initial, [hasTransitioned, state, initial]);

    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <circle
                xmlns="http://www.w3.org/2000/svg"
                cx="12"
                cy="12"
                r="3"
                fill="currentColor"
                className={cn({
                    "transition-colors duration-300 [transition-property:r]": animate,
                    "text-content-primary [r:3]": state === StepState.Waiting,
                    "text-content-yield [r:3]": state === StepState.Running,
                    "text-content-green [r:8]": state === StepState.Success,
                    "text-content-tertiary [r:3]": state === StepState.Empty,
                    "[r:0]": state === StepState.Failure || state === StepState.Warning,
                })}
            />
            <circle
                xmlns="http://www.w3.org/2000/svg"
                cx="12"
                cy="12"
                r="10"
                fill="currentColor"
                opacity={0.3}
                className={cn({
                    "transition-colors duration-150": animate,
                    "text-content-tertiary dark:text-content-primary": state === StepState.Waiting,
                    "duration-2000 animate-circlePulse text-content-yield": state === StepState.Running,
                    "text-content-green": state === StepState.Success,
                    "[r:0]": state === StepState.Empty || state === StepState.Failure || state === StepState.Warning,
                })}
            />
            {state === StepState.Success && (
                <path
                    d="M8 12.5L11 15L16 9"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    strokeDasharray={12}
                    strokeDashoffset={animate ? 12 : 0}
                    className={cn({
                        "animate-draw delay-500 duration-150": animate,
                    })}
                />
            )}
            {state === StepState.Warning && (
                // Explictly not re-using IconWarning as this is a slightly different warning icon. It's stroked rather than filled.
                <path
                    d="M12 9.36996V12.3154M12 14.3404V14.3331M10.1211 6.14899L5.4324 13.7308C4.52227 15.2025 5.58084 17.1018 7.31124 17.1018H16.6887C18.4191 17.1018 19.4777 15.2025 18.5675 13.7308L13.8788 6.14898C13.0154 4.75275 10.9846 4.75275 10.1211 6.14899ZM12.1841 14.3404C12.1841 14.4421 12.1017 14.5245 12 14.5245C11.8983 14.5245 11.8159 14.4421 11.8159 14.3404C11.8159 14.2387 11.8983 14.1564 12 14.1564C12.1017 14.1564 12.1841 14.2387 12.1841 14.3404Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    className="text-content-yield"
                />
            )}

            {state === StepState.Failure && <IconError size="lg" className="text-content-red" inline={true} />}
        </svg>
    );
};
